import React from 'react';
import {
    showNiceDate,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { PortfolioReturnsDataState } from 'widgets/portfolioReturns/store/stream/PortfolioReturnsStreamState';
import { PortfolioReturnsComponentState } from 'widgets/portfolioReturns/store/component/PortfolioReturnsComponentState';
import {
    buildPortfolioReturnsData,
    getYDomain,
    PortfolioReturnsDataItem,
} from 'widgets/portfolioReturns/chart/portfolioReturnsData';
import { getLines } from 'widgets/portfolioReturns/chart/porfolioReturnsSeries';
import { PortfolioReturnsConfigurationState } from 'widgets/portfolioReturns/store/configuration/PortfolioReturnsConfigurationState';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';

export interface WidgetPanelPortfolioReturnsProps
    extends WidgetPanelDetailProps {
    mode:
        | 'data'
        | 'stream'
        | 'timeSeries'
        | 'breaks'
        | 'available basis days'
        | 'range labels'
        | 'time line labels'
        | 'base';
}
export default function WidgetPanelPortfolioReturns(
    props: WidgetPanelPortfolioReturnsProps
) {
    const {
        widgetState,
        appSettings,
        mode,
        widget: { id: widgetKey },
    } = props;

    const data = widgetState.stream.data as PortfolioReturnsDataState;
    const component = widgetState.component as PortfolioReturnsComponentState;
    const config = widgetState.config as PortfolioReturnsConfigurationState;
    const { showBuysAsPositive } = appSettings;

    if (!data || !component) {
        return <></>;
    }
    const portfolioReturnsData = buildPortfolioReturnsData(
        component,
        showBuysAsPositive,
        data,
        !!config.invertSellLine
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (portfolioReturnsData as any).yDomain = buildYDomain(
        true,
        config,
        component,
        portfolioReturnsData.timeSeries,
        portfolioReturnsData.multipleSides
    );

    for (const b of portfolioReturnsData.timeSeries) {
        showNiceDate(b, 'time');
        showNiceDate(b, 'endOfBucketTime');
    }

    for (const r of portfolioReturnsData.rangeLabels) {
        showNiceDate(r, 'open');
        showNiceDate(r, 'close');
    }
    for (const r of portfolioReturnsData.breaksInTrading) {
        showNiceDate(r, 'from');
        showNiceDate(r, 'to');
    }

    for (const r of portfolioReturnsData.timeLineLabels) {
        showNiceDate(r, 'time');
        showNiceDate(r, 'from');
        showNiceDate(r, 'to');
    }
    showNiceDate(portfolioReturnsData.baseValues, 'time');

    switch (mode) {
        case 'data':
            return (
                <div className="widget-panel">
                    <div className="title">Portfolio Returns Data</div>
                    <JsonPanel data={portfolioReturnsData} />
                </div>
            );
        case 'stream':
            return (
                <WidgetPanelGrid
                    key="stream"
                    data={data.timeSeries}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-stream`}
                    colDefs={getTimeSeriesColDefs()}
                />
            );
        case 'timeSeries':
            return (
                <WidgetPanelGrid
                    key="timeSeries"
                    data={portfolioReturnsData!.timeSeries}
                    getRowDataId={(d) => d.time.toString()}
                    csvFileName={`${widgetKey}-timeSeries`}
                />
            );

        case 'range labels':
            return (
                <WidgetPanelGrid
                    key="rangeLabels"
                    data={portfolioReturnsData!.rangeLabels}
                    getRowDataId={(d) => d.open.toString()}
                    csvFileName={`${widgetKey}-rangeLabels`}
                />
            );

        case 'breaks':
            return (
                <WidgetPanelGrid
                    key="breaks"
                    data={portfolioReturnsData!.breaksInTrading ?? []}
                    getRowDataId={(d) => d.from.toString()}
                    csvFileName={`${widgetKey}-breaks`}
                />
            );
        case 'available basis days':
            return (
                <WidgetPanelGrid
                    key="available basis days"
                    data={portfolioReturnsData!.availableBasisDays ?? []}
                    getRowDataId={(d) => d.toString()}
                    csvFileName={`${widgetKey}-availableBasisDays`}
                />
            );
        case 'time line labels':
            return (
                <WidgetPanelGrid
                    key="time line labels"
                    data={portfolioReturnsData!.timeLineLabels ?? []}
                    getRowDataId={(d) => d.from.toString()}
                    csvFileName={`${widgetKey}-timeLineLabels`}
                />
            );
        case 'base':
            return <JsonPanel data={portfolioReturnsData.baseValues} />;

        default:
            return (
                <div className="widget-panel widget-panel-fills">
                    Unrecognised mode {mode}
                </div>
            );
    }
}

function buildYDomain(
    combineSellsAndShorts: boolean,
    config: PortfolioReturnsConfigurationState,
    componentState: PortfolioReturnsComponentState,
    timeSeries: PortfolioReturnsDataItem[],
    multipleSides: boolean
) {
    const lines = getLines(
        combineSellsAndShorts,
        config,
        componentState,
        multipleSides
    );

    type LimitItem = {
        time: number;
        displayTime: Date;
        value: number;
    };
    type LineLimit = {
        line: keyof PortfolioReturnsDataItem;
        min?: LimitItem;
        max?: LimitItem;
    };

    const limits: Record<keyof PortfolioReturnsDataItem, LineLimit> =
        lines.reduce(
            (acc, item) => {
                acc[item] = { line: item };
                return acc;
            },
            {} as Record<keyof PortfolioReturnsDataItem, LineLimit>
        );

    for (const item of timeSeries) {
        for (const line of lines) {
            const limit = limits[line];
            const value = item[line] as number | undefined;

            if (value !== undefined) {
                const limitItem = {
                    time: item.time,
                    displayTime: new Date(item.time),
                    value,
                };
                if (!limit.min || value < limit.min.value) {
                    limit.min = limitItem;
                }
                if (!limit.max || value > limit.max.value) {
                    limit.max = limitItem;
                }
            }
        }
    }

    return { yDomain: getYDomain(lines, timeSeries), limits };
}

export function addPortfolioReturnsPanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => (
        <WidgetPanelPortfolioReturns {...detailProps} mode={'data'} />
    );
    panes.push({
        menuItem: 'Data',
        render,
    });

    const renderStream = () => (
        <WidgetPanelPortfolioReturns {...detailProps} mode={'stream'} />
    );
    panes.push({
        menuItem: 'Stream',
        render: renderStream,
    });

    const renderTs = () => (
        <WidgetPanelPortfolioReturns {...detailProps} mode={'timeSeries'} />
    );
    panes.push({
        menuItem: 'Time Series',
        render: renderTs,
    });

    const renderAbd = () => (
        <WidgetPanelPortfolioReturns
            {...detailProps}
            mode={'available basis days'}
        />
    );
    panes.push({
        menuItem: 'Avail Basis Days',
        render: renderAbd,
    });

    const renderBr = () => (
        <WidgetPanelPortfolioReturns {...detailProps} mode={'breaks'} />
    );
    panes.push({
        menuItem: 'Breaks',
        render: renderBr,
    });

    const renderRl = () => (
        <WidgetPanelPortfolioReturns {...detailProps} mode={'range labels'} />
    );
    panes.push({
        menuItem: 'Range Labels',
        render: renderRl,
    });
    const renderTll = () => (
        <WidgetPanelPortfolioReturns
            {...detailProps}
            mode={'time line labels'}
        />
    );
    panes.push({
        menuItem: 'Time Line Labels',
        render: renderTll,
    });

    const renderBase = () => (
        <WidgetPanelPortfolioReturns {...detailProps} mode={'base'} />
    );
    panes.push({
        menuItem: 'Base Values',
        render: renderBase,
    });
}

function getTimeSeriesColDefs() {
    const cols = [
        '_i',
        'buyValue',
        'sellValue',
        'shortValue',
        'priceIndex1',
        'priceIndex2',
    ].map((key) => ({
        field: key,
        key,
        width: 120,
        resizable: true,
    }));

    return [
        {
            field: '_i',
            title: 'Time',
            width: 120,
            resizable: true,
            cellRenderer: (props: { value?: number }) => {
                return (
                    <span>
                        {props.value === undefined
                            ? ''
                            : new Date(props.value * 60000).toString()}
                    </span>
                );
            },
        },
        ...cols,
    ];
}
